import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-399e2554"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "panel custom-fields-panel p-bottom-4" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "panel-heading has-background-light has-text-weight-semibold is-size-4" }, " Additional information ", -1))
const _hoisted_3 = {
  key: 0,
  class: "panel-content"
}
const _hoisted_4 = { class: "panel-item is-flex is-align-items-center is-justify-content-space-between" }
const _hoisted_5 = { class: "has-text-weight-semibold is-flex is-align-items-center" }
const _hoisted_6 = { class: "info-icon m-left-8" }
const _hoisted_7 = { class: "panel-item" }
const _hoisted_8 = { class: "m-left-8 m-bottom-12" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = {
  key: 2,
  class: "is-italic"
}

import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation/nr-loading-animation.vue'
import RequestFieldEditModal from './RequestFieldEditModal.vue'
import {
  EditingAccess,
  RequestFieldType,
  RequestFieldValue
} from 'src/pages/request_show/store/request_state_declarations'
import { UserState } from 'src/store/globalModule'
import DOMPurify from 'dompurify'

interface RequestFieldValuesProps {
  fields: RequestFieldValue[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RequestFieldValues',
  props: {
    fields: {}
  },
  setup(__props: any) {

const props = __props
const store = useStore()

/**
 * Determines if the current user can edit a given field.
 *
 * Note: Direct permission logic is used here because CASL didn't properly evaluate
 * field.request_field_version.editing_access conditions despite correct backend configuration.
 * This manual check follows the same permission rules:
 * - Admins: can edit any field
 * - Publishers/Department Admins: can edit fields with editing_access 1 or 2
 * - Requesters: can edit their own fields if editing_access is 2
 */
const canEditField = (field: RequestFieldValue) => {
  const currentUser: UserState | null = store.state.currentUser
  if (!currentUser) {
    return false
  }

  const role = currentUser.user_role
  if (role === 'admin') {
    return true
  }

  const editingAccess = field.request_field_version?.editing_access
  if (editingAccess === undefined) {
    return false
  }

  if (
    (role === 'publisher' || role === 'department_admin') &&
    (editingAccess === EditingAccess.PUBLISHER ||
      editingAccess === EditingAccess.REQUESTER)
  ) {
    return true
  }

  if (editingAccess === EditingAccess.REQUESTER) {
    const isRequester = store.state.request_show?.request?.is_current_requester
    if (isRequester) {
      return true
    }
  }

  return false
}

const showEditModal = ref(false)
const selectedField = ref<RequestFieldValue | null>(null)

const filteredRequestLoading = computed(
  () => store.state.request_show.filteredRequestLoading
)

const openEditModal = (field: RequestFieldValue) => {
  selectedField.value = field
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  selectedField.value = null
}

const formatValue = (field: RequestFieldValue) => {
  switch (field.field_type) {
    case RequestFieldType.Text:
      return field.value || ''
    case RequestFieldType.Checkbox:
      return Array.isArray(field.value)
        ? field.value.map((option) => option.name).join(', ')
        : ''
    case RequestFieldType.Radio:
      return field.value?.name || ''
    case RequestFieldType.RichText:
      return DOMPurify.sanitize(field.value) || ''
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", _hoisted_1, [
    _hoisted_2,
    (!filteredRequestLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.fields, (field) => {
            return (_openBlock(), _createElementBlock("div", {
              key: field.id || `new-field-${field.field_id}`,
              class: "panel-content-section"
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("p", _hoisted_5, [
                  _createTextVNode(_toDisplayString(field.display_name) + " ", 1),
                  (
                field.id &&
                (field.field_settings_have_changed ||
                  field.field_value_was_edited)
              )
                    ? (_openBlock(), _createBlock(NrTooltip, {
                        key: 0,
                        label: "This field has changed since the making of the request"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_6, [
                            _createVNode(NrIconButton, {
                              variant: "minimal-neutral",
                              icon: { name: 'info-circle', type: 'regular' },
                              "aria-label": 'Field change information'
                            })
                          ])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                (canEditField(field))
                  ? (_openBlock(), _createBlock(NrTooltip, {
                      key: 0,
                      label: "Edit field",
                      class: "panel-item-edit"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(NrIconButton, {
                          variant: "minimal-neutral",
                          icon: { name: 'pencil-alt', type: 'regular' },
                          "aria-label": `Edit ${field.display_name}`,
                          onClick: ($event: any) => (openEditModal(field))
                        }, null, 8, ["aria-label", "onClick"])
                      ]),
                      _: 2
                    }, 1024))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("p", _hoisted_8, [
                  (
                field.value && field.field_type !== _unref(RequestFieldType).RichText
              )
                    ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(formatValue(field)), 1))
                    : (
                field.value && field.field_type === _unref(RequestFieldType).RichText
              )
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 1,
                          class: "rich-text-value",
                          innerHTML: formatValue(field)
                        }, null, 8, _hoisted_10))
                      : (_openBlock(), _createElementBlock("span", _hoisted_11, "No data entered"))
                ])
              ])
            ]))
          }), 128))
        ]))
      : (_openBlock(), _createBlock(NrLoadingAnimation, {
          key: 1,
          count: 3
        })),
    (selectedField.value && showEditModal.value)
      ? (_openBlock(), _createBlock(RequestFieldEditModal, {
          key: 2,
          field: selectedField.value,
          onClose: closeEditModal
        }, null, 8, ["field"]))
      : _createCommentVNode("", true)
  ]))
}
}

})